// src/scenes/sitemanager/DashboardFinance.jsx
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
    Card,
    CardContent,
    Button
} from "@mui/material";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend
} from "recharts";
import StatBox from "../../component-files/StatBox";
import Header from "../../component-files/Header";
import { tokens } from "../../theme";
import { useAuth } from "../../context/AuthContext";

const API_BASE = "https://figazuresql.swancreate.com/api/swa";

export default function DashboardFinance() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { allocations, isSchoolUser } = useAuth();

    // --- filters ---
    const [siteFilter, setSiteFilter] = useState("");
    const [mealFilter, setMealFilter] = useState("");
    const [yearFilter, setYearFilter] = useState("");
    const [monthFilter, setMonthFilter] = useState("");

    // --- raw ADP data + dropdowns ---
    const [adpData, setAdpData] = useState([]);
    const [sites, setSites] = useState([]);
    const [schoolYears, setSchoolYears] = useState([]);

    // distinct service types for chart & leaderboards
    const serviceTypes = useMemo(
        () => Array.from(new Set(adpData.map((r) => r.ServiceType))).sort(),
        [adpData]
    );

    // --- fetch & initialize on mount or when allocations change ---
    useEffect(() => {
        axios.get(`${API_BASE}/vw_ADP_Monthly`).then((res) => {
            const data = res.data;
            setAdpData(data);

            // 1) only sites you’re allowed
            const siteMap = {};
            data.forEach((r) => {
                if (!isSchoolUser || allocations.some((a) => a.SchoolId === r.SchoolId)) {
                    siteMap[r.SchoolId] = r.SchoolName;
                }
            });
            const allowed = Object.entries(siteMap)
                .map(([id, name]) => ({ id, name }))
                .sort((a, b) => a.name.localeCompare(b.name));
            setSites(allowed);

            // default to first allowed site
            if (allowed.length && !allowed.find((s) => s.id === siteFilter)) {
                setSiteFilter(allowed[0].id);
            }

            // 2) build school-year list (Jul–Jun)
            const years = Array.from(
                new Set(
                    data.map((r) => {
                        const [y, m] = r.YearMonth.split("-").map(Number);
                        const sy = m < 7 ? y - 1 : y;
                        return `${sy}-${sy + 1}`;
                    })
                )
            ).sort();
            setSchoolYears(years);

            // default to first year
            if (years.length && !years.includes(yearFilter)) {
                setYearFilter(years[0]);
            }
        });
    }, [allocations, isSchoolUser, siteFilter, yearFilter]);

    // --- filtered ADP for selected site/year/month ---
    const filteredAdp = useMemo(() => {
        const [syStart] = yearFilter.split("-").map(Number);
        return adpData.filter((r) => {
            const [y, m] = r.YearMonth.split("-").map(Number);
            const inYear = (m < 7 ? y - 1 : y) === syStart;
            return (
                inYear &&
                (!siteFilter || r.SchoolId === siteFilter) &&
                (!mealFilter || r.ServiceType === mealFilter) &&
                (!monthFilter || r.YearMonth === monthFilter)
            );
        });
    }, [adpData, siteFilter, mealFilter, yearFilter, monthFilter]);

    // --- district‐wide ADP slice ---
    const districtAdp = useMemo(() => {
        const [syStart] = yearFilter.split("-").map(Number);
        return adpData.filter((r) => {
            const [y, m] = r.YearMonth.split("-").map(Number);
            const inYear = (m < 7 ? y - 1 : y) === syStart;
            return (
                inYear &&
                (!mealFilter || r.ServiceType === mealFilter) &&
                (!monthFilter || r.YearMonth === monthFilter)
            );
        });
    }, [adpData, mealFilter, yearFilter, monthFilter]);

    // --- averages for stat boxes ---
    const avgSiteAdp = useMemo(() => {
        if (!filteredAdp.length) return 0;
        return Math.round(
            filteredAdp.reduce((sum, r) => sum + r.ADP_MonthlyPct, 0) /
            filteredAdp.length
        );
    }, [filteredAdp]);

    const avgDistrictAdp = useMemo(() => {
        if (!districtAdp.length) return 0;
        return Math.round(
            districtAdp.reduce((sum, r) => sum + r.ADP_MonthlyPct, 0) /
            districtAdp.length
        );
    }, [districtAdp]);

    // --- ranking map for district sites ---
    const districtMap = useMemo(() => {
        const m = {};
        districtAdp.forEach((r) => {
            m[r.SchoolId] = m[r.SchoolId] || { name: r.SchoolName, total: 0, count: 0 };
            m[r.SchoolId].total += r.ADP_MonthlyPct;
            m[r.SchoolId].count++;
        });
        return Object.values(m)
            .map((g) => ({
                site: g.name,
                avg: g.count ? Math.round(g.total / g.count) : 0
            }))
            .sort((a, b) => b.avg - a.avg);
    }, [districtAdp]);

    const siteRank = useMemo(() => {
        const name = sites.find((s) => s.id === siteFilter)?.name || "";
        const idx = districtMap.findIndex((d) => d.site === name);
        return idx >= 0 ? idx + 1 : "--";
    }, [districtMap, siteFilter, sites]);

    // --- build chart data ---
    const chartData = useMemo(() => {
        const m = {};
        filteredAdp.forEach((r) => {
            if (!m[r.YearMonth]) m[r.YearMonth] = { YearMonth: r.YearMonth };
            m[r.YearMonth][r.ServiceType] = r.ADP_MonthlyPct;
        });
        return Object.values(m).sort((a, b) => {
            const [ay, am] = a.YearMonth.split("-").map(Number);
            const [by, bm] = b.YearMonth.split("-").map(Number);
            return (am < 7 ? ay - 1 : ay) * 12 + am - ((bm < 7 ? by - 1 : by) * 12 + bm);
        });
    }, [filteredAdp]);

    // --- per-service leaderboards + CSV export ---
    const serviceLeaders = useMemo(() => {
        const out = {};
        serviceTypes.forEach((type) => {
            const bySite = {};
            filteredAdp
                .filter((r) => r.ServiceType === type)
                .forEach((r) => {
                    bySite[r.SchoolId] =
                        bySite[r.SchoolId] || { name: r.SchoolName, total: 0, count: 0 };
                    bySite[r.SchoolId].total += r.ADP_MonthlyPct;
                    bySite[r.SchoolId].count++;
                });
            out[type] = Object.values(bySite)
                .map((g) => ({
                    site: g.name,
                    avg: g.count ? Math.round(g.total / g.count) : 0
                }))
                .sort((a, b) => b.avg - a.avg);
        });
        return out;
    }, [filteredAdp, serviceTypes]);

    const downloadCSV = (rows, name) => {
        const csv = ["site,adp", ...rows.map((r) => `${r.site},${r.avg}`)].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = name;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Box p={3}>
            <Header title="ADP Dashboard" subtitle="Average Daily Participation" />

            {/* Top cards */}
            <Box display="flex" gap={2} mb={4}>
                <Card
                    sx={{
                        flex: 3,
                        p: 4,
                        borderRadius: 2,
                        border: `1px solid ${colors.greenAccent[400]}`,
                        boxShadow: `0 0 20px ${colors.greenAccent[600]}`
                    }}
                >
                    <Typography variant="h1" sx={{ color: colors.primary[100], mb: 1 }}>
                        {avgDistrictAdp}%
                    </Typography>
                    <Typography variant="subtitle1" sx={{ color: colors.greenAccent[200] }}>
                        District Avg ADP (%)
                    </Typography>
                </Card>
                <Card
                    sx={{
                        flex: 2,
                        p: 4,
                        borderRadius: 2,
                        border: `1px solid ${colors.blueAccent[300]}`,
                        boxShadow: `0 0 20px ${colors.blueAccent[500]}`
                    }}
                >
                    <Typography variant="h6" sx={{ color: colors.primary[100] }}>
                        {sites.find((s) => s.id === siteFilter)?.name || "All Sites"}
                    </Typography>
                    <Typography variant="h1" sx={{ color: colors.blueAccent[100], my: 1 }}>
                        {siteRank}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: colors.primary[200] }}>
                        rank out of {districtMap.length}
                    </Typography>
                    <Box
                        sx={{
                            height: 4,
                            width: "50%",
                            backgroundColor: colors.primary[100],
                            borderRadius: 2,
                            mt: 1,
                            mb: 2,
                            mx: "auto"
                        }}
                    />
                    <Typography
                        variant="h2"
                        sx={{ color: colors.blueAccent[100], textAlign: "center", mb: 0.5 }}
                    >
                        {avgSiteAdp}%
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        sx={{ color: colors.blueAccent[200], textAlign: "center" }}
                    >
                        Avg ADP (%)
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: colors.blueAccent[200], mt: 1, textAlign: "center" }}
                    >
                        Keep up the great work!
                    </Typography>
                </Card>
            </Box>

            {/* Filters */}
            <Box display="flex" gap={2} mb={4} flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Site</InputLabel>
                    <Select
                        value={siteFilter}
                        onChange={(e) => setSiteFilter(e.target.value)}
                    >
                        {sites.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                                {s.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Meal Service</InputLabel>
                    <Select
                        value={mealFilter}
                        onChange={(e) => setMealFilter(e.target.value)}
                    >
                        <MenuItem value="">All Services</MenuItem>
                        {serviceTypes.map((t) => (
                            <MenuItem key={t} value={t}>
                                {t}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>School Year</InputLabel>
                    <Select
                        value={yearFilter}
                        onChange={(e) => setYearFilter(e.target.value)}
                    >
                        {schoolYears.map((y) => (
                            <MenuItem key={y} value={y}>
                                {y}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Month</InputLabel>
                    <Select
                        value={monthFilter}
                        onChange={(e) => setMonthFilter(e.target.value)}
                    >
                        <MenuItem value="">All</MenuItem>
                        {chartData.map((d) => (
                            <MenuItem key={d.YearMonth} value={d.YearMonth}>
                                {d.YearMonth}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* ADP Line Chart */}
            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Typography variant="h6" mb={2}>
                        Monthly ADP by Service Type
                    </Typography>
                    <Box height={350}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                                data={chartData}
                                margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="YearMonth" />
                                <YAxis domain={[0, 100]} />
                                <Tooltip />
                                <Legend verticalAlign="top" height={36} />
                                {serviceTypes.map((type) => {
                                    const color = {
                                        Breakfast: colors.redAccent[400],
                                        Lunch: colors.blueAccent[400],
                                        Snack: colors.purpleAccent?.[300] || "rgba(204,153,255,0.7)",
                                        Supper: colors.greenAccent[400]
                                    }[type];
                                    return (
                                        <Line
                                            key={type}
                                            type="monotone"
                                            dataKey={type}
                                            stroke={color}
                                            strokeWidth={3}
                                            dot={{ r: 4, stroke: color, strokeWidth: 2 }}
                                            style={{ filter: `drop-shadow(0 0 8px ${color})` }}
                                        />
                                    );
                                })}
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                </CardContent>
            </Card>

            {/* Leaderboards */}
            <Box display="grid" gridTemplateColumns="repeat(4,1fr)" gap={2}>
                {serviceTypes.map((type) => (
                    <Card key={type} sx={{ boxShadow: `0 0 15px ${colors.primary[800]}` }}>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="h6">{type} Leaders</Typography>
                                <Button
                                    size="small"
                                    onClick={() => downloadCSV(serviceLeaders[type], `${type}_leaderboard.csv`)}
                                >
                                    Download CSV
                                </Button>
                            </Box>
                            <Box component="ol" sx={{ pl: 2, m: 0 }}>
                                {serviceLeaders[type].map((d, i) => (
                                    <Typography
                                        key={d.site}
                                        component="li"
                                        sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}
                                    >
                                        <span>{`${i + 1}. ${d.site}`}</span>
                                        <span>{d.avg}%</span>
                                    </Typography>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                ))}
            </Box>
        </Box>
    );
}
